import {
  CascaderField,
  DateRangePickerField,
  Form,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import type { OrganizationFormSchema } from '@/features/tenant-manager/organization/_models/organization';
import { useForm } from '@/hooks';
import { isRS } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useRoles } from '../../role/_hooks/role';
import { useOrganizationOptionsRecursive } from '../_hooks/organization';
import { organizationFormValuesParser } from '../_utils/organization';

type FormValues = z.infer<typeof OrganizationFormSchema>;

type Props = {
  id?: string;
  initialValues?: Partial<FormValues>;
  isIntegrated?: boolean;
  onSubmit: (values: FormValues) => void;
};

export const OrganizationForm: FC<Props> = ({
  initialValues,
  isIntegrated,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(organizationFormValuesParser),
  });
  const options = useOrganizationOptionsRecursive();
  const roleOptions = useRoles((data) =>
    data.map((d) => ({ value: d.id, label: d.name })),
  );

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      {!isRS() && (
        <TextInputField
          name="externalSystemCode"
          autocompleteKey="organization.externalSystemCode"
          label={t('organization-code')}
          control={control}
          errors={errors}
          disabled={isIntegrated}
          required
        />
      )}
      <TextInputField
        name="code"
        autocompleteKey="organization.code"
        label={t('organization-control-code')}
        control={control}
        errors={errors}
        disabled={isIntegrated}
        required
      />
      <TextInputField
        name="name"
        autocompleteKey="organization.name"
        label={t('name')}
        control={control}
        errors={errors}
        disabled={isIntegrated}
        required
      />
      <CascaderField
        name="upperOrganizationIds"
        label={t('parent-organization-name')}
        control={control}
        errors={errors}
        options={options.data}
        disabled={isIntegrated}
        loading={options.isPending}
        enableIntermediate
      />
      <SelectField
        name="roleId"
        options={roleOptions.data}
        label={t('role-name')}
        control={control}
        errors={errors}
        loading={roleOptions.isPending}
      />
      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
        disabled={isIntegrated}
      />
      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
